import { ConditionalLink } from "components/conditionalLink";
import { Button } from "components/DesignSystem/Button/Button";
import Modal from "components/DesignSystem/Modal/Modal";
import { TextInput } from "components/DesignSystem/TextInput/TextInput";
import AddEntity from "components/Entity/addMoreEntity";
import { Redirect } from "components/icons/Redirect";
import { Star } from "components/icons/Star";
import { INKLE_WEBSITE_URL } from "constants/base";
import {
  CLICKED_ON_INTER_COMPANY_TRANSACTIONS_GET_STARTED_CTA,
  CLICKED_ON_SCHEDULE_A_DEMO_CALL_INSIDE_RESOURCES,
  CLICKED_ON_VIEW_BREAKUP_CTA_IN_CALCULATOR,
  COMPLETE_TP_KYC,
  ENTERED_AMOUNT_IN_SEND_IN_FX_CALCULATOR,
} from "constants/analyticsEvents";
import {
  SUPPORT_TP_CALENDLY_LINK,
  SQUAREFEET_BLOG_LINK,
  SWIFT_BLOG_LINK,
  TP_COMPLIANCE_CALENDLY_LINK,
} from "constants/tppayments";
import { Form, Formik, FormikValues } from "formik";
import { useAnalytics } from "hooks/useAnalytics";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { useToast } from "hooks/useToast";
import { useWindowSize } from "hooks/useWindowSize";
import { useEffect, useState } from "react";
import ReactCountryFlag from "react-country-flag";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useRouteMatch } from "react-router-dom";
import BookIconTP from "static/images/BookIconTP.svg";
import VideoCameraIcon from "static/images/VideoCameraIcon.svg";
import {
  useCreateEntityMutation,
  useLazyGetEntitiesQuery,
} from "store/apis/group";
import { useCreateOnboardingTaskMutation } from "store/apis/task";
import { useLazyGetGroupDataQuery } from "store/apis/teamSetting";
import {
  useGetTPAccountInfoQuery,
  useLazyGetQuoteQuery,
} from "store/apis/transferPricing";
import { setDefaultChannel } from "store/slices/chat";
import { setQuoteResponse } from "store/slices/transferpricing";
import { RootState } from "store/store";
import { TPOnboardingCard } from "TPOnboardingCard";
import { currency } from "utils/Currency";
import { debounce } from "utils/debouncing";
import { AmountInput } from "../TransferModal/AmountInput";
import { AmountStep } from "../TransferModal/AmountStep";
import { TPInfoModal } from "./TPInfoModal";
import { TPPaymentBanner } from "pages/TPPayments/TPPaymentsBanner";
export const Illustration = ({
  isOnboardingPending,
}: {
  isOnboardingPending: boolean;
}) => {
  const { alertToast } = useToast();
  const { quote } = useSelector((state: RootState) => state.tpPaymentsSlice);
  const [showTPInfo, setShowTPInfo] = useState<boolean>(false);
  const [showAddEntity, setShowAddEntity] = useState(false);
  const [showAddEntityConfirm, setShowAddEntityConfirm] = useState(false);
  const [openTOSModal, setOpenTOSModal] = useState(false);
  const [isDispatched, setIsDispatched] = useState(false);
  const [showDetailedView, setShowDetailedView] = useState(false);
  const history = useHistory();
  const { trackEvent } = useAnalytics();
  const { isMobile } = useWindowSize();
  const redirectIconSize = isMobile ? 14 : 18;
  const { isAdmin } = useRoleBasedView();
  const [getConvertedAmount] = useLazyGetQuoteQuery();
  const dispatch = useDispatch();
  const { url } = useRouteMatch();
  const [createEntity, { isLoading: isCreating }] = useCreateEntityMutation();

  const {
    secondary_channel_url,
    uuid: groupId,
    entities,
    name: companyName,
    dispatch: groupDispatch,
  } = useCurrentGroupContext();

  const { data: accountInfo } = useGetTPAccountInfoQuery(
    { groupId },
    { skip: !groupId }
  );

  const [createTask, { isLoading: isTaskCreating }] =
    useCreateOnboardingTaskMutation();

  const [getGroupData] = useLazyGetEntitiesQuery();

  const [getGroupDataCRM] = useLazyGetGroupDataQuery();

  const receiverAmount = quote?.receiver_amount
    ? currency({
        amount: Number(quote?.receiver_amount),
        currencyCode: "INR",
      }).replace("₹", "")
    : "";

  const onCompleteKyc = async () => {
    if (accountInfo?.tpx_task_uuid) {
      history.push(`/filings/${accountInfo?.tpx_task_uuid}`);
      setIsDispatched(false);
      return;
    }

    const indianEntities = entities.filter((e) => e.country_code === "IN");

    if (indianEntities.length > 0) {
      const payload = {
        task_template_id: accountInfo?.tpx_task_template_uuid,
        title: "Transfer Pricing Payments Onboarding",
        payment_category: 0,
        payment_amount: "",
        company_entity_ids: [indianEntities[0].uuid],
        adhoc_scope_of_work: "",
      };
      const res = await createTask({ payload }).unwrap();
      trackEvent(COMPLETE_TP_KYC, {
        company_name: companyName,
      });
      history.push(`/filings/${res.uuid}`);
      dispatch(setDefaultChannel(secondary_channel_url));
      setIsDispatched(false);
    } else {
      dispatch(setDefaultChannel(secondary_channel_url));
      setShowAddEntityConfirm(true);
      setIsDispatched(false);
    }
  };

  const setGroupData = async () => {
    if (isAdmin) {
      try {
        const response = await getGroupDataCRM({ groupId }).unwrap();
        groupDispatch({ type: "SET_GROUP", payload: response });
        setShowAddEntity(false);
        setIsDispatched(true);
      } catch (e: any) {
        alertToast({ message: e?.data?.error?.message });
        setShowAddEntity(false);
      }
    } else {
      try {
        const response = await getGroupData({}).unwrap();
        dispatch({ type: "SET_GROUP", payload: response });
        setShowAddEntity(false);
        setIsDispatched(true);
      } catch (e: any) {
        alertToast({ message: e?.data?.error?.message });
        setShowAddEntity(false);
      }
    }
  };

  const addEntityData = async (values: FormikValues) => {
    try {
      let entityData = {
        company_name: values.company_name,
        formation_type_id: values.formation_type_id,
        country_id: values.country,
        state_id: values.state_id,
      };
      await createEntity({ groupId, payload: entityData }).unwrap();
      setGroupData();
      setShowAddEntity(false);
    } catch (e: any) {
      setShowAddEntity(false);
      alertToast({ message: e?.data?.error?.message });
    }
  };

  const handleOnChange = debounce(async (e: any) => {
    const amount = e.target.value?.replaceAll(",", "");
    trackEvent(ENTERED_AMOUNT_IN_SEND_IN_FX_CALCULATOR);
    try {
      if (amount && !isNaN(+amount)) {
        const resp = await getConvertedAmount({ amount }).unwrap();
        dispatch(setQuoteResponse(resp));
      }
    } catch (e: any) {
      alertToast({ message: e?.data?.error?.message });
    }
  });

  const isOnboardingPendingHandler = () => {
    trackEvent(CLICKED_ON_INTER_COMPANY_TRANSACTIONS_GET_STARTED_CTA);
    if (accountInfo?.tos_accepted) {
      onCompleteKyc();
    } else {
      setOpenTOSModal(true);
    }
  };

  const handleViewBreakupClick = () => {
    setShowDetailedView(true);
    trackEvent(CLICKED_ON_VIEW_BREAKUP_CTA_IN_CALCULATOR);
  };

  useEffect(() => {
    if (isDispatched) onCompleteKyc();
  }, [isDispatched]);

  const steps = [
    " Do a 10-min KYC to activate a US Virtual Account",
    "Make a US domestic wire to the Virtual Account",
    "Money gets credited to Indian Bank Account in T+1 Days",
  ];

  return (
    <>
      <TPPaymentBanner
        isOnboardingPending={isOnboardingPending}
        steps={steps}
        isOnboardingPendingHandler={isOnboardingPendingHandler}
      />

      <div className="t-mt-6 t-flex t-gap-4 sm:t-flex-col">
        <div className="t-rounded-lg t-flex t-flex-col t-border t-border-solid t-border-neutral-10 t-w-1/3 sm:t-w-full t-pt-6 t-pb-5 t-px-4 t-gap-5">
          <div className="t-text-subtitle">FX Calculator</div>
          <div className="t-flex t-flex-col t-gap-3">
            <div className="t-flex t-gap-6">
              <Formik
                initialValues={{
                  amount: quote?.sender_amount,
                  inramount: "",
                }}
                onSubmit={() => {}}
              >
                <Form
                  onChange={handleOnChange}
                  className="all:unset t-flex t-flex-col t-gap-6 t-w-full"
                >
                  <AmountInput isInitiateTransferModal={false} />
                  <TextInput
                    label="Get (indicative amount)"
                    name="inramount"
                    disabled
                    value={receiverAmount}
                    icon={
                      <div className="t-flex t-items-center t-gap-2">
                        <ReactCountryFlag
                          countryCode="IN"
                          svg
                          style={{ width: "20px", height: "24px" }}
                        />
                        <div>INR</div>
                      </div>
                    }
                  />
                </Form>
              </Formik>
            </div>
            <div className="t-text-body-sm t-text-text-30 t-ml-0.5">
              Disclaimer: The conversion rate may vary depending on the exact
              time of transaction.
            </div>
            <Button onClick={handleViewBreakupClick}>View breakup</Button>
          </div>
        </div>
        <div className="t-rounded-lg t-border t-border-solid t-border-neutral-10 t-w-2/3 sm:t-w-full t-p-6 sm:t-p-4 t-flex t-flex-col t-gap-7">
          <div className="t-flex t-flex-col t-gap-1">
            <div className="t-text-subtitle">Resources</div>
            <div className="t-text-body-sm t-text-text-30">
              Inkle resources to help you get started
            </div>
          </div>
          <div className="t-flex t-flex-col t-gap-6 t-text-body-sm">
            <ConditionalLink
              to={SUPPORT_TP_CALENDLY_LINK}
              className="all:unset t-cursor-pointer hover:!t-underline hover:t-text-purple"
            >
              <span
                className="t-flex t-gap-2 t-items-center"
                onClick={() =>
                  trackEvent(CLICKED_ON_SCHEDULE_A_DEMO_CALL_INSIDE_RESOURCES)
                }
              >
                <img
                  src={VideoCameraIcon}
                  alt="CameraIcon"
                  className="t-shrink-0"
                  width="20px"
                  height="20px"
                />
                <span>Schedule a demo call with us</span>
                <span className="t-flex t-shrink-0">
                  <Redirect color="currentColor" size={redirectIconSize} />
                </span>
              </span>
            </ConditionalLink>
            <ConditionalLink
              to={TP_COMPLIANCE_CALENDLY_LINK}
              className="all:unset t-cursor-pointer hover:!t-underline hover:t-text-purple"
            >
              <span className="t-flex t-gap-2 t-items-center">
                <img
                  src={VideoCameraIcon}
                  alt="CameraIcon"
                  className="t-shrink-0"
                  width="20px"
                  height="20px"
                />
                <span>Schedule a Transfer Pricing Compliance Health Check</span>
                <span className="t-flex t-shrink-0">
                  <Redirect color="currentColor" size={redirectIconSize} />
                </span>
              </span>
            </ConditionalLink>
            <ConditionalLink
              to={SWIFT_BLOG_LINK}
              className="all:unset t-cursor-pointer hover:!t-underline hover:t-text-purple"
            >
              <span className="t-flex t-gap-2 t-items-center">
                <img
                  src={BookIconTP}
                  alt="Book"
                  width="20px"
                  height="20px"
                  className="t-shrink-0"
                />
                <span>How is this different from SWIFT to Indian bank?</span>
                <span className="t-flex t-shrink-0">
                  <Redirect color="currentColor" size={redirectIconSize} />
                </span>
              </span>
            </ConditionalLink>
            <ConditionalLink
              to={SQUAREFEET_BLOG_LINK}
              className="all:unset t-cursor-pointer hover:!t-underline hover:t-text-purple"
            >
              <span className="t-flex t-gap-2 t-items-center">
                <img
                  src={BookIconTP}
                  alt="Book"
                  width="20px"
                  height="20px"
                  className="t-shrink-0"
                />
                <span>
                  Read a case study on how 91SquareFeet scaled TP transactions
                  with Inkle
                </span>
                <span className="t-flex t-shrink-0">
                  <Redirect color="currentColor" size={redirectIconSize} />
                </span>
              </span>
            </ConditionalLink>
            <ConditionalLink
              to={`${INKLE_WEBSITE_URL}blog/unlocking-global-trade-the-role-of-purpose-codes-in-cross-border-payments`}
              className="all:unset t-cursor-pointer hover:!t-underline hover:t-text-purple"
            >
              <span className="t-flex t-gap-2 t-items-center">
                <img
                  src={BookIconTP}
                  alt="Book"
                  width="20px"
                  height="20px"
                  className="t-shrink-0"
                />
                <span>
                  Unlocking Global Trade: The Role of Purpose Codes in
                  Cross-Border Payments
                </span>
                <span className="t-flex t-shrink-0">
                  <Redirect color="currentColor" size={redirectIconSize} />
                </span>
              </span>
            </ConditionalLink>
          </div>
        </div>
      </div>
      <TPInfoModal showTPInfo={showTPInfo} setShowTPInfo={setShowTPInfo} />
      <TPOnboardingCard
        onCompleteKyc={onCompleteKyc}
        setShowAddEntity={setShowAddEntity}
        showAddEntityConfirm={showAddEntityConfirm}
        setShowAddEntityConfirm={setShowAddEntityConfirm}
        setOpenTOSModal={setOpenTOSModal}
        openTOSModal={openTOSModal}
      />
      <AddEntity
        addEntityData={addEntityData}
        showAddEntity={showAddEntity}
        setShowAddEntity={setShowAddEntity}
        isCreating={isCreating}
      />
      <Modal.Root open={showDetailedView} onOpenChange={setShowDetailedView}>
        <Modal.Content>
          <Modal.Header className="sm:t-pl-4">
            <Modal.Title>Transfer money</Modal.Title>
            <Modal.Close />
          </Modal.Header>
          <Modal.Body className="sm:t-px-4">
            <AmountStep backPath={url} isInitiateTransferModal={false} />
          </Modal.Body>
          <Modal.Footer>
            <Button
              customType="primary"
              block
              isLoading={isTaskCreating}
              disabled={isTaskCreating}
              onClick={
                isOnboardingPending ? isOnboardingPendingHandler : () => {}
              }
            >
              Get started
            </Button>
          </Modal.Footer>
        </Modal.Content>
      </Modal.Root>
    </>
  );
};
